import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input, FormField } from '../../components/ui/Input';
import { authApi } from '../../api/auth';
import { useToast } from '../../contexts/ToastContext';
import { ApiError } from '../../api/client';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const nav = useNavigate();
  const toast = useToast();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <Card className="w-full max-w-md relative !p-8">
        <div className="text-center">
          <Link to="/" className="font-display font-bold text-2xl text-gradient">
            AltCoinsMoney
          </Link>
          <h1 className="font-display text-2xl font-bold mt-6 text-danger">
            Missing reset token
          </h1>
          <p className="text-ink-600 text-sm mt-4">
            This page requires a valid reset link. Request a new one if needed.
          </p>
          <div className="mt-6">
            <Link to="/auth/forgot-password" className="text-violet-600 hover:underline">
              Request a new link
            </Link>
          </div>
        </div>
      </Card>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await authApi.resetPassword({ token, newPassword: password });
      toast.success('Password updated. Sign in with your new password.');
      nav('/auth/login');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Reset failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md relative !p-8">
      <div className="text-center mb-8">
        <Link to="/" className="font-display font-bold text-2xl text-gradient">
          AltCoinsMoney
        </Link>
        <h1 className="font-display text-3xl font-bold mt-6">Set new password</h1>
        <p className="text-ink-600 text-sm mt-2">Choose a strong password (8+ characters)</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField label="New password" htmlFor="password">
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
        </FormField>
        <FormField label="Confirm new password" htmlFor="confirm">
          <Input
            id="confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
        </FormField>

        {error && (
          <div className="p-3 rounded-2xl bg-danger/5 border border-danger/20 text-danger text-sm">
            {error}
          </div>
        )}

        <Button type="submit" isLoading={loading} className="w-full">
          Update password
        </Button>
      </form>
    </Card>
  );
}
